/**
 * useAvailability — owns GET /api/v1/utilisation/availability (the governed denominator
 * toggle). TanStack Query + the carried AuthContext token (CD-7: hooks fetch). The 200 body
 * is an AvailabilityView (the same operating-hours numerator under three availability bases);
 * a 422 arrives as the ApiError the card renders verbatim. Group is served pooled, not refused.
 *
 * useAvailabilityAll fetches all three sites for one period (Group/Kirkstall/Oracle) so the
 * headline tiles can recast under the selected basis. React Query dedupes by queryKey, so the
 * scoped card's own useAvailability call shares the cache — no double request.
 */

import { useQueries, useQuery, type UseQueryResult } from '@tanstack/react-query'
import { useAuth } from '../contexts/AuthContext'
import type { AvailabilitySeries, AvailabilityView } from '../types/availability'
import type { Site } from '../types/metrics'
import { ApiError, fetchApi } from './apiFetch'

const ALL_SITES: Site[] = ['Group', 'Kirkstall', 'Oracle']
const path = (site: Site, period: string): string =>
  `utilisation/availability?${new URLSearchParams({ site, period }).toString()}`

export function useAvailability(
  site: Site,
  period: string
): UseQueryResult<AvailabilityView, ApiError> {
  const { token } = useAuth()
  return useQuery<AvailabilityView, ApiError>({
    queryKey: ['availability', site, period],
    queryFn: () => fetchApi<AvailabilityView>(path(site, period), token),
    enabled: token !== null && period !== '',
  })
}

export interface AvailabilityAll {
  bySite: Partial<Record<Site, AvailabilityView>>
  isLoading: boolean
}

/** All three sites for one period — feeds the headline tiles' basis recast. */
export function useAvailabilityAll(period: string): AvailabilityAll {
  const { token } = useAuth()
  const results = useQueries({
    queries: ALL_SITES.map((site) => ({
      queryKey: ['availability', site, period],
      queryFn: () => fetchApi<AvailabilityView>(path(site, period), token),
      enabled: token !== null && period !== '',
    })),
  })
  const bySite: Partial<Record<Site, AvailabilityView>> = {}
  results.forEach((r, i) => {
    if (r.data) bySite[ALL_SITES[i]] = r.data as AvailabilityView
  })
  return { bySite, isLoading: results.some((r) => r.isLoading) }
}

/** Utilisation by month across a window for one site — the legible per-month capacity view. */
export function useAvailabilitySeries(
  site: Site,
  from: string,
  to: string
): UseQueryResult<AvailabilitySeries, ApiError> {
  const { token } = useAuth()
  const params = new URLSearchParams({ site, period_from: from, period_to: to })
  return useQuery<AvailabilitySeries, ApiError>({
    queryKey: ['availability-series', site, from, to],
    queryFn: () => fetchApi<AvailabilitySeries>(`utilisation/availability/series?${params.toString()}`, token),
    enabled: token !== null && from !== '' && to !== '',
  })
}
